import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { EncodeMeshGeometryToDraco } from '@/engine/Utilities/DracoUtils';

import { Vector3 } from './MCSD/vector3';
import { Visualization } from './MCSD/visualization';
import { Mesh } from './MCSD/mesh';
import { Spline } from './MCSD/spline';
import { Point } from './MCSD/point';
import { Line } from './MCSD/line';
import { Plane } from './MCSD/plane';
import { MCSData } from './MCSD/m-c-s-data';

import * as THREE from 'three';
import * as flatbuffers from 'flatbuffers';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import type { IExporter } from './IExporter';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { randomColorRGB } from '@/engine/utils/colorUtils';
import { sha256 } from '@/engine/utils/hash';

export class MCSDExporter implements IExporter {
  private readonly _algo: PlanningAlgorithm;

  private readonly _vertices: Array<string>;

  public constructor(algo: PlanningAlgorithm, vertices: Array<string>) {
    this._algo = algo;
    this._vertices = vertices;
  }

  public async export(): Promise<Blob> {
    const zip = new JSZip();
    const builder = new flatbuffers.Builder(1024);
    const meshes: Array<number> = [];
    const polylines: Array<number> = [];
    const points: Array<number> = [];
    const lines: Array<number> = [];
    const planes: Array<number> = [];
    for (const name of this._vertices) {
      const type = this._algo.getType(name);
      if (type == DT.Mesh.Type) {
        const mesh = this._algo.getValue(name, DT.Mesh);
        if (mesh) {
          const descriptor = await sha256(name);
          const position = mesh.value.hasAttribute('position') ? mesh.value.attributes.position.array : undefined;
          const index = mesh.value.hasAttribute('index') ? mesh.value.attributes.index.array : undefined;
          if (position && position.length > 0 && index && index.length > 0) {
            let geometry = mesh.value;
            if (this._algo.has(name + '.cs')) {
              const cs = this._algo.getValue(name + '.cs', DT.TransformMatrix);
              if (cs && !cs.value.equals(new THREE.Matrix4().identity())) {
                geometry = geometry.clone().applyMatrix4(cs.value);
              }
            }
            zip.file(
              descriptor + '.drc',
              new Blob([EncodeMeshGeometryToDraco(geometry)], {
                type: 'octet/stream'
              }),
              { base64: false, binary: true }
            );
          }
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Mesh.startMesh(builder);
          Mesh.addGuid(builder, d);
          Mesh.addLabel(builder, n);
          Mesh.addColor(builder, randomColorRGB());
          Mesh.addIsVisible(builder, true);
          Mesh.addVisualization(builder, Visualization.BasicColor);
          meshes.push(Mesh.endMesh(builder));
        }
      } else if (type == DT.Polyline.Type) {
        const polyline = this._algo.getValue(name, DT.Polyline);
        if (polyline && polyline.value.points.length > 1) {
          const descriptor = await sha256(name);
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Spline.startBasePointsVector(builder, polyline.value.points.length);
          for (const pt of polyline.value.points) Vector3.createVector3(builder, pt.x, pt.y, pt.z);
          const b = builder.endVector();
          Spline.startSpline(builder);
          Spline.addGuid(builder, d);
          Spline.addLabel(builder, n);
          Spline.addColor(builder, randomColorRGB());
          Spline.addIsVisible(builder, true);
          Spline.addBasePoints(builder, b);
          Spline.addIsClosed(builder, polyline.value.isClosed);
          polylines.push(Spline.endSpline(builder));
        }
      } else if (type == DT.MatPolyline.Type) {
        const polyline = this._algo.getValue(name, DT.MatPolyline);
        if (polyline && polyline.value.accessPoints().size() > 1) {
          const pts = polyline.value.accessPoints();
          const descriptor = await sha256(name);
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Spline.startBasePointsVector(builder, pts.size());
          for (let i = 0, sz = pts.size(); i < sz; ++i) {
            const pt = pts.access(i);
            Vector3.createVector3(builder, pt.x, pt.y, pt.z);
          }
          const b = builder.endVector();
          Spline.startSpline(builder);
          Spline.addGuid(builder, d);
          Spline.addLabel(builder, n);
          Spline.addColor(builder, randomColorRGB());
          Spline.addIsVisible(builder, true);
          Spline.addBasePoints(builder, b);
          Spline.addIsClosed(builder, polyline.value.closed);
          polylines.push(Spline.endSpline(builder));
        }
      } else if (type == DT.Point.Type) {
        const point = this._algo.getValue(name, DT.Point);
        if (point) {
          const descriptor = await sha256(name);
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Point.startPoint(builder);
          Point.addGuid(builder, d);
          Point.addLabel(builder, n);
          Point.addColor(builder, randomColorRGB());
          Point.addIsVisible(builder, true);
          Point.addPtCoordinates(builder, Vector3.createVector3(builder, point.value.x, point.value.y, point.value.z));
          points.push(Point.endPoint(builder));
        }
      } else if (type == DT.Line.Type) {
        const line = this._algo.getValue(name, DT.Line);
        if (line) {
          const descriptor = await sha256(name);
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Line.startLine(builder);
          Line.addGuid(builder, d);
          Line.addLabel(builder, n);
          Line.addColor(builder, randomColorRGB());
          Line.addIsVisible(builder, true);
          Line.addPtFrom(
            builder,
            Vector3.createVector3(builder, line.value.start.x, line.value.start.y, line.value.start.z)
          );
          Line.addPtTo(builder, Vector3.createVector3(builder, line.value.end.x, line.value.end.y, line.value.end.z));
          lines.push(Line.endLine(builder));
        }
      } else if (type == DT.Plane.Type) {
        const plane = this._algo.getValue(name, DT.Plane);
        if (plane) {
          const descriptor = await sha256(name);
          const n = builder.createString(name);
          const d = builder.createString(descriptor);
          Plane.startPlane(builder);
          Plane.addGuid(builder, d);
          Plane.addLabel(builder, n);
          Plane.addColor(builder, randomColorRGB());
          Plane.addIsVisible(builder, true);
          Plane.addPtOrigin(
            builder,
            Vector3.createVector3(builder, plane.value.origin.x, plane.value.origin.y, plane.value.origin.z)
          );
          Plane.addVecNormal(
            builder,
            Vector3.createVector3(
              builder,
              plane.value.planeEq.normal.x,
              plane.value.planeEq.normal.y,
              plane.value.planeEq.normal.z
            )
          );
          Plane.addWidth(builder, 50);
          Plane.addHeight(builder, 50);
          planes.push(Plane.endPlane(builder));
        }
      } else if (type == DT.AreaMeasurement.Type) {
        const areaMeas = this._algo.getValue(name, DT.AreaMeasurement);
        const color = randomColorRGB();
        if (areaMeas) {
          // splinePts
          const suffix = '_splinePts';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Spline.startBasePointsVector(builder, areaMeas.splinePts.length);
          for (const pt of areaMeas.splinePts) Vector3.createVector3(builder, pt.x, pt.y, pt.z);
          const b = builder.endVector();
          Spline.startSpline(builder);
          Spline.addGuid(builder, d);
          Spline.addLabel(builder, n);
          Spline.addColor(builder, color);
          Spline.addIsVisible(builder, true);
          Spline.addBasePoints(builder, b);
          Spline.addIsClosed(builder, true);
          polylines.push(Spline.endSpline(builder));
        }
        if (areaMeas) {
          // basePts
          const suffix = '_basePts';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Spline.startBasePointsVector(builder, areaMeas.basePts.length);
          for (const pt of areaMeas.basePts) Vector3.createVector3(builder, pt.x, pt.y, pt.z);
          const b = builder.endVector();
          Spline.startSpline(builder);
          Spline.addGuid(builder, d);
          Spline.addLabel(builder, n);
          Spline.addColor(builder, color);
          Spline.addIsVisible(builder, false);
          Spline.addBasePoints(builder, b);
          Spline.addIsClosed(builder, true);
          polylines.push(Spline.endSpline(builder));
        }
        if (areaMeas) {
          // plane
          const suffix = '_plane';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Plane.startPlane(builder);
          Plane.addGuid(builder, d);
          Plane.addLabel(builder, n);
          Plane.addColor(builder, color);
          Plane.addIsVisible(builder, false);
          Plane.addPtOrigin(
            builder,
            Vector3.createVector3(builder, areaMeas.center.x, areaMeas.center.y, areaMeas.center.z)
          );
          Plane.addVecNormal(
            builder,
            Vector3.createVector3(builder, areaMeas.normal.x, areaMeas.normal.y, areaMeas.normal.z)
          );
          Plane.addWidth(builder, 50);
          Plane.addHeight(builder, 50);
          planes.push(Plane.endPlane(builder));
        }
        if (areaMeas) {
          // center
          const suffix = '_center';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Point.startPoint(builder);
          Point.addGuid(builder, d);
          Point.addLabel(builder, n);
          Point.addColor(builder, color);
          Point.addIsVisible(builder, false);
          Point.addPtCoordinates(
            builder,
            Vector3.createVector3(builder, areaMeas.center.x, areaMeas.center.y, areaMeas.center.z)
          );
          points.push(Point.endPoint(builder));
        }
        if (areaMeas?.lMin) {
          // lMin
          const suffix = '_lMin';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Line.startLine(builder);
          Line.addGuid(builder, d);
          Line.addLabel(builder, n);
          Line.addColor(builder, color);
          Line.addIsVisible(builder, false);
          Line.addPtFrom(
            builder,
            Vector3.createVector3(builder, areaMeas.lMin.start.x, areaMeas.lMin.start.y, areaMeas.lMin.start.z)
          );
          Line.addPtTo(
            builder,
            Vector3.createVector3(builder, areaMeas.lMin.end.x, areaMeas.lMin.end.y, areaMeas.lMin.end.z)
          );
          lines.push(Line.endLine(builder));
        }
        if (areaMeas?.lMax) {
          // lMax
          const suffix = '_lMax';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Line.startLine(builder);
          Line.addGuid(builder, d);
          Line.addLabel(builder, n);
          Line.addColor(builder, color);
          Line.addIsVisible(builder, false);
          Line.addPtFrom(
            builder,
            Vector3.createVector3(builder, areaMeas.lMax.start.x, areaMeas.lMax.start.y, areaMeas.lMax.start.z)
          );
          Line.addPtTo(
            builder,
            Vector3.createVector3(builder, areaMeas.lMax.end.x, areaMeas.lMax.end.y, areaMeas.lMax.end.z)
          );
          lines.push(Line.endLine(builder));
        }
        if (areaMeas?.orientationY) {
          // orientationY
          const suffix = '_orientationY';
          const descriptor = await sha256(name + suffix);
          const n = builder.createString(name + suffix);
          const d = builder.createString(descriptor);
          Line.startLine(builder);
          Line.addGuid(builder, d);
          Line.addLabel(builder, n);
          Line.addColor(builder, color);
          Line.addIsVisible(builder, false);
          Line.addPtFrom(
            builder,
            Vector3.createVector3(builder, areaMeas.center.x, areaMeas.center.y, areaMeas.center.z)
          );
          Line.addPtTo(
            builder,
            Vector3.createVector3(
              builder,
              areaMeas.center.x + areaMeas.orientationY.x,
              areaMeas.center.y + areaMeas.orientationY.y,
              areaMeas.center.z + +areaMeas.orientationY.z
            )
          );
          lines.push(Line.endLine(builder));
        }
      }
    }
    const mds = MCSData.createMeshesVector(builder, meshes);
    const pol = MCSData.createSplinesVector(builder, polylines);
    const pts = MCSData.createPointsVector(builder, points);
    const lns = MCSData.createLinesVector(builder, lines);
    const pls = MCSData.createPlanesVector(builder, planes);
    MCSData.startMCSData(builder);
    MCSData.addMeshes(builder, mds);
    MCSData.addSplines(builder, pol);
    MCSData.addPoints(builder, pts);
    MCSData.addLines(builder, lns);
    MCSData.addPlanes(builder, pls);
    const dfvd = MCSData.endMCSData(builder);
    builder.finish(dfvd);
    const header = builder.asUint8Array();
    zip.file('CaseHeader.mcsd', header, { base64: false, binary: true });
    return zip.generateAsync({ type: 'blob' });
  }

  public async download(): Promise<void> {
    const zip = await this.export();
    FileSaver.saveAs(zip, 'Data.zip');
  }
}
